import React from 'react';
import { SerializedStyles, css } from '@emotion/react';
import { ModalInterface } from './types';
import { useTheme } from '@veneer/theme';
import { hideFocusRing } from '../utils/focus_ring';

const useStyles = ({
  align,
  alignFooter,
  maxWidth,
}: {
  align: ModalInterface['align'];
  alignFooter: ModalInterface['alignFooter'];
  maxWidth: ModalInterface['maxWidth'];
}): {
  overlay: {
    base: SerializedStyles;
    show: SerializedStyles;
  };
  root: {
    base: SerializedStyles;
    expanded: SerializedStyles;
    hc: SerializedStyles;
  };
  title: SerializedStyles;
  body: SerializedStyles;
  align: SerializedStyles;
  footer: {
    base: SerializedStyles;
    alignFooter: SerializedStyles;
    align: SerializedStyles;
  };
  header: {
    base: SerializedStyles;
    align: SerializedStyles;
  };
  closeButton: SerializedStyles;
  pill: {
    root: SerializedStyles;
    closeButton: SerializedStyles;
  };
} => {
  const theme = useTheme();

  return React.useMemo(
    () => ({
      overlay: {
        base: css({
          backgroundColor: theme.color.effects.glasswall,
          backdropFilter: `blur(${theme.elevation.glasswall.blur}px)`,
          zIndex: theme.elevation.modal,
          display: 'grid',
          placeItems: 'center',
          opacity: 0,

          transitionProperty: 'opacity',
          transitionDuration: '150ms',
          transitionTimingFunction: 'ease-in-out',
        }),
        show: css({
          opacity: 1,
        }),
      },
      root: {
        base: css({
          boxSizing: 'border-box',
          display: 'inline-block',
          position: 'relative',
          textAlign: align,
          verticalAlign: 'middle',

          color: theme.color.foreground.default,
          backgroundColor: theme.color.background.container.default,
          borderRadius: theme.borderRadius5,
          boxShadow: `${theme.elevation.level20.boxShadow} ${theme.color.effects.dropShadow}`,

          padding: `${theme.orbitalModalVertical} calc(${theme.orbitalModalHorizontal})`,
          [`@media (max-width: ${theme.layout.smMin})`]: {
            padding: `${theme.orbitalModalVertical} calc(${theme.orbitalModalHorizontal} - 8px)`,
          },

          maxWidth:
            maxWidth ||
            `calc(400px + ${theme.orbitalModalHorizontal} + ${theme.orbitalModalHorizontal})`,
          width: `calc(400px + ${theme.orbitalModalHorizontal} + ${theme.orbitalModalHorizontal})`,

          [`@media (max-width: ${theme.layout.mdMax})`]: {
            width: `calc(100% - ${theme.layout.size6} - ${theme.layout.size6})`,
          },

          zIndex: theme.elevation.modal,
          ...hideFocusRing(),
        }),
        expanded: css({
          maxWidth:
            maxWidth ||
            `calc(100% - ${theme.layout.size6} - ${theme.layout.size6})`,
          width: `calc(100% - ${theme.layout.size6} - ${theme.layout.size6})`,
        }),
        hc: css({
          borderColor: theme.color.foreground.default,
          borderStyle: 'solid',
          borderWidth: '1px',
        }),
      },
      title: css({
        paddingBottom: theme.layout.size5,

        [`@media (max-width: ${theme.layout.smMin})`]: {
          paddingBottom: theme.layout.size3,
        },
      }),
      body: css({
        '> p:not(:last-of-type)': {
          paddingBottom: theme.orbitalEight,

          [`@media (max-width: ${theme.layout.smMin})`]: {
            paddingBottom: theme.layout.size6,
          },
        },
      }),
      align: css({
        justifyContent: align,
        alignItems: align,
      }),
      footer: {
        base: css({
          display: 'flex',
          paddingTop: theme.orbitalModalVertical,

          [`@media (max-width: ${theme.layout.smMin})`]: {
            paddingTop: theme.layout.size6,
          },
        }),
        alignFooter: css({
          justifyContent: alignFooter,
        }),
        align: css({
          justifyContent: align,
        }),
      },
      header: {
        base: css({
          display: 'flex',
          paddingBottom: theme.orbitalModalVertical,

          [`@media (max-width: ${theme.layout.smMin})`]: {
            paddingBottom: theme.layout.size6,
          },
        }),
        align: css({
          justifyContent: align,
        }),
      },
      closeButton: css({
        position: 'absolute',
        top: theme.orbitalTwo,
        right: theme.orbitalTwo,
      }),
      pill: {
        root: css({
          borderRadius: theme.borderRadius10,
        }),
        closeButton: css({
          top: theme.orbitalThree,
          right: theme.orbitalThree,
        }),
      },
    }),
    [align, alignFooter, maxWidth, theme]
  );
};

export default useStyles;
